import { ReactElement } from 'react';
import {
    Field,
    FieldProps,
    Form,
    Formik,
    validateYupSchema,
    yupToFormErrors,
    type FormikErrors,
    type FormikTouched,
} from 'formik';
import cn from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import styles from './StepConfirmData.module.scss';
import cms from '../../../data/cms.json';
import config from '../../../config.json';
import { Step } from '../../Step/Step';
import { ValuesProps } from '../Step6/Step6';
import { Button } from '../../Button/Button';
import { scrollToError } from '../../../utils/scrollToError';
import { Box } from '../../Box/Box';
import { Grid } from '../../Grid/Grid';
import { Label } from '../../Label/Label';
import { formatDate } from '../../../utils/formatDate';
import { ErrorForm } from '../../ErrorForm/ErrorForm';
import { CheckButton } from '../../CheckButton/CheckButton';
import { PEPPopup } from '../Step2/PopupContent';
import { Select } from '../../Select/Select';
import { validationSchemaDefault } from './validations';
import { validateField } from '../../../utils/validations';
import { FormItemError } from '../../FormItemError/FormItemError';
import { AmountButton } from '../../AmountButton/AmountButton';
import DatePicker from 'react-date-picker';
import PhoneNumberInputNew from '../../PhoneNumberInput/PhoneNumberInput';
import { FormikTextInput } from '../../TextInput/FormikTextInput';
import { DataPlaceholder } from '../../DataPlaceholder/DataPlaceholder';
import { IdCardType, participantInitialValues } from './utils';
import { useStepConfirmData } from './useStepConfirmData';
import dayjs from 'dayjs';
import { Icon } from '../../Icon/Icon';

interface StepConfirmDataProps {
    className?: string;
}

export const StepConfirmData = ({ className }: StepConfirmDataProps): ReactElement | null => {
    const {
        hasBirthplace,
        hasIssuer,
        participant,
        isSubmitting,
        isSupportedPrefix,
        submitted,
        setSubmitted,
        onSubmit,
        isPhoneLoading,
        phoneNumber,
        constructSelectCountriesOptions,
        constructSelectBankCodesOptions,
        showSecondCitizenship,
        setShowSecondCitizenship,
        renderAddress,
        isPep,
        handleIsPepClick,
        bankIdUrl,
        ctx,
        idType,
        hasBirthCountry,
        birthCountry,
        hasCitizenship,
        citizenship,
        hasSecondCitizenship,
        secondCitizenship,
        issueDate,
        idCardCountryCode,
        IdCard,
        idCardKindCode,
    } = useStepConfirmData();

    const idCountry = (touched: FormikTouched<ValuesProps>, errors: FormikErrors<ValuesProps>) =>
        idCardCountryCode ? (
            <DataPlaceholder label="Země vydání" data-test="idCountryPlaceholder">
                {constructSelectCountriesOptions().find((country) => country.value === idCardCountryCode)?.label}
            </DataPlaceholder>
        ) : (
            <div>
                <Label data-test="idCountryLabel">Země vydání</Label>
                <Field
                    id="idCardCountry"
                    name="idCardCountry"
                    className={cn(styles.select)}
                    options={constructSelectCountriesOptions()}
                    component={Select}
                    validate={(value: string | number) => validateField(value, false)}
                    error={errors.idCardCountry}
                    data-test="country"
                />
                {errors.idCardCountry && touched.idCardCountry && (
                    <FormItemError text={errors.idCardCountry as string} data-test="idCountryError" />
                )}
            </div>
        );

    return (
        <Step
            title={cms.step_confirmdata.title}
            text={cms.step_confirmdata.text}
            className={cn(styles.wrapper, className)}
        >
            <Formik
                enableReinitialize
                initialValues={participantInitialValues(participant, isSupportedPrefix)}
                validateOnBlur={false}
                validateOnChange={submitted}
                validateOnMount={false}
                validate={(values) => {
                    !submitted && setSubmitted(true);
                    try {
                        validateYupSchema(values, validationSchemaDefault(!!issueDate), true, values);
                    } catch (err) {
                        return yupToFormErrors(err);
                    }
                    return {};
                }}
                onSubmit={onSubmit}
            >
                {({ values, errors, touched, handleSubmit }: ValuesProps) => (
                    <Form className={styles.form}>
                        <Box innerWrapper loading={isSubmitting} data-test="confirmBox">
                            <Grid cols={2} className="mb-8">
                                <FormikTextInput label="Titul před" name="titleBefore" data-test="titleBefore" />
                                <FormikTextInput label="Titul za" name="titleAfter" data-test="titleAfter" />
                            </Grid>
                            <Grid cols={2} className={'mb-8'}>
                                <DataPlaceholder label="Jméno" data-test="firstName">
                                    {participant.Identification.PersonalIdentificationDetail?.FirstName}
                                </DataPlaceholder>
                                <DataPlaceholder label="Příjmení" data-test="lastName">
                                    {participant.Identification.PersonalIdentificationDetail?.LastName}
                                </DataPlaceholder>
                            </Grid>
                            <Grid cols={2} className={'mb-8'}>
                                <FormikTextInput
                                    label="E-mail"
                                    name="email"
                                    error={touched.email && errors.email}
                                    data-test="email"
                                />
                                <div style={{ maxWidth: '20rem', margin: '0.5rem auto' }}>
                                    <PhoneNumberInputNew
                                        className="field-large"
                                        namePhone="phone"
                                        namePhonePrefix="phonePrefix"
                                        label={
                                            ctx.insuranceType === config.MORTGAGE_CODE ? '<b>Telefon</b>' : 'Telefon'
                                        }
                                        error={(touched.phone && errors.phone) || ''}
                                        optional={false}
                                        loading={isPhoneLoading}
                                        data-test="phone"
                                    />
                                    {!isSupportedPrefix && !isPhoneLoading && !touched.phone && (
                                        <>
                                            <div></div>
                                            <FormItemError
                                                text={`Telefonní číslo ${phoneNumber} není námi podporováno, prosíme o vyplnění telefonního čísla včetně předvolby výše.`}
                                                data-test="phoneError"
                                            />
                                        </>
                                    )}
                                </div>
                            </Grid>
                            <Grid cols={2} className={'mb-8'}>
                                <DataPlaceholder label="Datum narození" data-test="birthDt">
                                    {formatDate(participant.Identification.BirthDt)}
                                </DataPlaceholder>
                                {hasBirthplace ? (
                                    <DataPlaceholder label="Místo narození" data-test="birthPlace">
                                        {participant.Identification.PersonalIdentificationDetail?.Birthplace}
                                    </DataPlaceholder>
                                ) : (
                                    <FormikTextInput
                                        label="Místo narození"
                                        name="birthplace"
                                        error={touched.birthplace && errors.birthplace}
                                        data-test="birthPlace"
                                    />
                                )}
                            </Grid>
                            <Grid cols={2} className={'mb-8'}>
                                {hasBirthCountry ? (
                                    <DataPlaceholder label="Stát narození" data-test="birthCountry">
                                        {birthCountry}
                                    </DataPlaceholder>
                                ) : (
                                    <div>
                                        <Label data-test="birthCountryLabel">Stát narození</Label>
                                        <Field
                                            id="birthCountry"
                                            name="birthCountry"
                                            className={cn(styles.select)}
                                            options={constructSelectCountriesOptions()}
                                            component={Select}
                                            validate={(value: string | number) => validateField(value, false)}
                                            error={errors.birthCountry}
                                            data-test="birthCountry"
                                        />
                                        {errors.birthCountry && touched.birthCountry && (
                                            <FormItemError
                                                text={errors.birthCountry as string}
                                                data-test="birthCountryError"
                                            />
                                        )}
                                    </div>
                                )}
                                {hasCitizenship ? (
                                    <DataPlaceholder label="Státní občanství" data-test="citizenship">
                                        {citizenship}
                                    </DataPlaceholder>
                                ) : (
                                    <div>
                                        <Label data-test="citizenshipLabel">Státní občanství</Label>
                                        <Field
                                            id="citizenship"
                                            name="citizenship"
                                            className={cn(styles.select)}
                                            options={constructSelectCountriesOptions()}
                                            component={Select}
                                            error={errors.citizenship}
                                            data-test="citizenship"
                                        />
                                    </div>
                                )}
                            </Grid>
                            <Grid cols={1} className={'mb-8'}>
                                {showSecondCitizenship || hasSecondCitizenship ? (
                                    <>
                                        {hasSecondCitizenship ? (
                                            <DataPlaceholder
                                                label="Druhé státní občanství"
                                                data-test="secondCitizenship"
                                            >
                                                {secondCitizenship}
                                            </DataPlaceholder>
                                        ) : (
                                            <>
                                                <Label data-test="secondCitizenshipLabel">Druhé státní občanství</Label>
                                                <div className={cn(styles.toggleForm)}>
                                                    <Field
                                                        id="secondCitizenship"
                                                        name="secondCitizenship"
                                                        className={cn(styles.select, styles.nomargin)}
                                                        options={constructSelectCountriesOptions()}
                                                        component={Select}
                                                        error={errors.secondCitizenship}
                                                        data-test="secondCitizenship"
                                                    />
                                                    <AmountButton
                                                        className={cn(styles.ml1, 'addMore text--left')}
                                                        title={cms.common.removeItemButton}
                                                        actionType="decrease"
                                                        onClick={() => {
                                                            setShowSecondCitizenship(false);
                                                            values['validateSecondCitizenship'] = false;
                                                            values['secondCitizenship'] = null;
                                                        }}
                                                        data-test="moreSecondCitizenship"
                                                    />
                                                </div>
                                                {errors.secondCitizenship && touched.secondCitizenship && (
                                                    <FormItemError
                                                        text={errors.secondCitizenship as string}
                                                        data-test="secondCitizenshipError"
                                                    />
                                                )}
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <div className={styles.toggleForm}>
                                        <AmountButton
                                            className="addMore"
                                            title={cms.common.addItemButton}
                                            actionType="increase"
                                            onClick={() => {
                                                setShowSecondCitizenship(true);
                                                values['validateSecondCitizenship'] = true;
                                            }}
                                            data-test="moreCitizenship"
                                        />
                                        <div className={styles.label} data-test="moreCitizenshipLabel">
                                            Přidat další občanství
                                        </div>
                                    </div>
                                )}
                            </Grid>
                            <Grid cols={1} className={'mb-8'}>
                                <div>
                                    <Label data-test="permanentAddressLabel">Trvalá adresa</Label>
                                    {renderAddress(participant.Identification.PermanentAddress)}
                                </div>
                            </Grid>
                            {participant.Identification.ContactAddress &&
                                participant.Identification.ContactAddress?.CityName.length > 0 && (
                                    <Grid cols={1} className={'mb-8'}>
                                        <div>
                                            <Label>Kontaktní adresa</Label>
                                            {renderAddress(participant.Identification.ContactAddress)}
                                        </div>
                                    </Grid>
                                )}
                            <Label className="text--bold" data-test="idCardLabel">
                                Doklad
                            </Label>
                            <Grid cols={2} className={'mb-8'}>
                                <DataPlaceholder label="Typ dokladu" data-test="idCardType">
                                    {idType}
                                </DataPlaceholder>
                                <DataPlaceholder label="Číslo dokladu" data-test="idCardNo">
                                    {IdCard?.IdCardNo}
                                </DataPlaceholder>
                            </Grid>
                            <Grid cols={2} className={'mb-8'}>
                                <div className="flex-col flex-a-center">
                                    <Label data-test="issueDateLabel">Vydán kdy</Label>
                                    {issueDate && issueDate !== null ? (
                                        <div className={styles.boxValue} data-test="issueDate">
                                            {formatDate(issueDate)}
                                        </div>
                                    ) : (
                                        <>
                                            <Field id="issueDate" name="issueDate" error={errors.issueDate}>
                                                {({ field }: FieldProps) => (
                                                    <DatePicker
                                                        {...field}
                                                        name="issueDate"
                                                        // onChange={async (value: Date) => {
                                                        //     await form.setFieldValue(field.name, value);
                                                        // }}
                                                        format="dd-MM-yyyy"
                                                        disableCalendar
                                                        dayPlaceholder="dd"
                                                        monthPlaceholder="mm"
                                                        yearPlaceholder="rrrr"
                                                        maxDate={dayjs().year(9999).toDate()}
                                                        locale="cs-CZ"
                                                        required
                                                        className={errors.issueDate && submitted ? styles.dtError : ''}
                                                        data-test="issueDate"
                                                    />
                                                )}
                                            </Field>
                                        </>
                                    )}
                                    {errors.issueDate && submitted && (
                                        <FormItemError text={errors.issueDate} data-test="issueDateError" />
                                    )}
                                </div>
                                <DataPlaceholder label="Platnost do" data-test="expireDate">
                                    {formatDate(IdCard?.ExpiryDt)}
                                </DataPlaceholder>
                            </Grid>
                            <Grid cols={2} className={'mb-8'}>
                                <FormikTextInput
                                    label="Vydán kým"
                                    name="issuer"
                                    readonly={!!hasIssuer}
                                    error={touched.issuer && errors.issuer}
                                    data-test="issuer"
                                />
                                {!idCardCountryCode && idCardKindCode === IdCardType.PP ? (
                                    <DataPlaceholder label="Země vydání" data-test="issuerCountry">
                                        Česká republika
                                    </DataPlaceholder>
                                ) : (
                                    idCountry(touched, errors)
                                )}
                            </Grid>
                            <Label className="text--bold" data-test="bankAccountLabel">
                                Bankovní účet
                            </Label>
                            <Grid cols={3} className={'mb-8'}>
                                <FormikTextInput
                                    label="Předčíslí"
                                    name="bankAccountPrefix"
                                    variant="number"
                                    thousandSeparator=""
                                    data-test="bankAccountPrefix"
                                />
                                <FormikTextInput
                                    label="Číslo účtu"
                                    name="bankAccountNo"
                                    variant="number"
                                    thousandSeparator=""
                                    error={touched.bankAccountNo && errors.bankAccountNo}
                                    data-test="bankAccountNumber"
                                />
                                <div key="bankAccountCode">
                                    <Label htmlFor="bankAccountCode" data-test="bankCodeLabel">
                                        Banka
                                    </Label>
                                    <Field
                                        id="bankAccountCode"
                                        name="bankAccountCode"
                                        className={cn(styles.select)}
                                        options={constructSelectBankCodesOptions()}
                                        component={Select}
                                        error={errors.bankAccountCode}
                                        data-test="bankCode"
                                    />
                                </div>
                                {((errors.bankAccountNo && touched.bankAccountNo) ||
                                    (errors.bankAccountCode && touched.bankAccountCode)) && (
                                    <>
                                        <div></div>
                                        {errors.bankAccountNo && touched.bankAccountNo && (
                                            <FormItemError
                                                text={errors.bankAccountNo as string}
                                                data-test="bankAccountNumberError"
                                            />
                                        )}
                                        {errors.bankAccountCode && touched.bankAccountCode && (
                                            <FormItemError
                                                text={errors.bankAccountCode as string}
                                                data-test="bankAccountCodeError"
                                            />
                                        )}
                                    </>
                                )}
                            </Grid>
                            <Grid cols={1} className={'mb-8'}>
                                <div className={styles.toggleForm}>
                                    <CheckButton
                                        className={styles.checkButton}
                                        onClick={() => handleIsPepClick()}
                                        size="small"
                                        checked={isPep}
                                        data-test="isPep"
                                    />
                                    <div
                                        className={styles.label}
                                        onClick={() => handleIsPepClick()}
                                        data-test="isPepLabel"
                                    >
                                        Já nebo blízká osoba je Politicky exponovaná
                                    </div>
                                    <div
                                        className={cn(styles.help, 'help')}
                                        onClick={() => {
                                            ctx.setPopupContent(PEPPopup);
                                            ctx.setPopupContentVariables({ uncloseable: false });
                                            ctx.setCloseButton(true);
                                            ctx.setPopupVisible(true);
                                        }}
                                        data-test="isPepHelp"
                                    >
                                        <Icon name="info" />
                                    </div>
                                </div>
                            </Grid>
                            <Button
                                className="mb-6"
                                variant="primary"
                                loading={isSubmitting}
                                onClick={(e) => {
                                    e?.preventDefault();
                                    handleSubmit();
                                    scrollToError();
                                }}
                                data-test="confirmButton"
                            >
                                {cms.common.confirmButton}
                            </Button>
                            <div className={styles.newBankId} data-test="bankId">
                                <div className={styles.text} data-test="bankIdText">
                                    {cms.common.bugBankId + ' '}
                                    <span
                                        className={styles.decoration}
                                        onClick={(e) => {
                                            e?.preventDefault();
                                            if (ctx.formResult) {
                                                bankIdUrl &&
                                                    window &&
                                                    window.open(bankIdUrl.replace('{#nonce}', uuidv4()), '_self');
                                            }
                                        }}
                                        data-test="resetBankId"
                                    >
                                        {cms.common.resetBankId}
                                    </span>
                                </div>
                            </div>
                        </Box>
                        <ErrorForm />
                    </Form>
                )}
            </Formik>
        </Step>
    );
};
